import type {
  ConditionFieldDescription,
  ConditionFunctionConfig,
  IntegrationDescription,
  ValueConfig,
} from "@smartsuite/automation-engine-api";
import { KeyValueCustomSelect } from "@smartsuite/react-ui/lib";
import { t } from "i18next";
import { Fragment, useState } from "react";
import * as R from "remeda";
import { useTheme } from "../../../../../../stores";
import type { CandidateStore } from "../../../../../../stores/automation/candidate.store";
import { TriggerLabel } from "../../../../../../utils/constants";
import { getLocalCssColorVariables } from "../../../../../../utils/cssVariablesUtils";
import { ArgumentEditor } from "../../../../Forms/InputForm/ArgumentEditor";
import { getFunctionConfig } from "../ConditionItem/utils/function-utils";
import type { FnConfig } from "../ConditionItem/utils/types";
import { Argument, Root, Section, Separator } from "./style";

export const ChangeFn: React.FC<{
  inputIdPrefix?: string;
  conditionFn?: ConditionFunctionConfig;
  availableFns: FnConfig[];
  fieldDescription?: ConditionFieldDescription;
  candidate: CandidateStore;
  integrations: IntegrationDescription[];
  onChange: (conditionFn: ConditionFunctionConfig) => void;
}> = ({
  inputIdPrefix = "condition-change",
  conditionFn,
  availableFns,
  candidate,
  integrations,
  fieldDescription,
  onChange,
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const args =
    conditionFn?.function.oneofKind === undefined
      ? []
      : getArgList(conditionFn);

  const handleArgChange = (
    functionName: string,
    argName: string,
    value: unknown,
  ): void => {
    if (conditionFn?.function.oneofKind === undefined || functionName === "") {
      return;
    }

    onChange({
      function: {
        ...conditionFn.function,
        [functionName]: {
          // @ts-expect-error indexed access on oneof payload
          ...conditionFn.function[functionName],
          [argName]: value,
        },
      },
    });
  };

  return (
    <>
      <Root>
        <Section>
          <KeyValueCustomSelect
            state={getSelectorState(
              candidate,
              open,
              conditionFn?.function.oneofKind,
            )}
            onOpenedChange={setOpen}
            showClear={false}
            value={conditionFn?.function.oneofKind}
            placeholder={open ? t("automation-form.select-function") : ""}
            placeholderArrowStyle="filled-dark"
            searchPlaceholder={t("general.search")}
            portalStyles={getLocalCssColorVariables(theme)}
            options={availableFns.map(([key, value]) => ({
              label: value,
              value: key,
            }))}
            onChange={(value: unknown): void => {
              onChange(getSelectedFunction(String(value)));
            }}
          />
        </Section>

        {args.length === 1 && (
          <Argument>
            {args.map((arg) => (
              <Fragment key={arg.name}>
                <Separator />
                {R.isTruthy(fieldDescription) && (
                  <ArgumentEditor
                    key={`${inputIdPrefix}-${arg.name}-${
                      conditionFn?.function.oneofKind ?? "empty"
                    }`}
                    inputId={`${inputIdPrefix}-${
                      conditionFn?.function.oneofKind ?? "empty"
                    }-${arg.name}`}
                    value={arg.value}
                    fieldDescription={fieldDescription}
                    functionConfig={getFunctionConfig(
                      availableFns,
                      conditionFn?.function.oneofKind,
                    )}
                    candidate={candidate}
                    integrations={integrations}
                    onChange={(value) =>
                      handleArgChange(
                        conditionFn?.function.oneofKind ?? "",
                        arg.name,
                        value,
                      )
                    }
                  />
                )}
              </Fragment>
            ))}
          </Argument>
        )}
      </Root>
    </>
  );
};

const getSelectedFunction = (value: string): ConditionFunctionConfig => {
  switch (value) {
    case "isEquals":
      return {
        function: {
          oneofKind: "isEquals",
          isEquals: {},
        },
      };
    case "isNotEquals":
      return {
        function: {
          oneofKind: "isNotEquals",
          isNotEquals: {},
        },
      };
    case "isAnyOf":
      return {
        function: {
          oneofKind: "isAnyOf",
          isAnyOf: {},
        },
      };
    case "isNoneOf":
      return {
        function: {
          oneofKind: "isNoneOf",
          isNoneOf: {},
        },
      };
    case "isEmpty":
      return {
        function: {
          oneofKind: "isEmpty",
          isEmpty: {},
        },
      };
    case "isNotEmpty":
      return {
        function: {
          oneofKind: "isNotEmpty",
          isNotEmpty: {},
        },
      };
    case "isAny":
      return {
        function: {
          oneofKind: "isAny",
          isAny: {},
        },
      };
    case "hasAnyOf":
      return {
        function: {
          oneofKind: "hasAnyOf",
          hasAnyOf: {},
        },
      };
    case "hasAllOf":
      return {
        function: {
          oneofKind: "hasAllOf",
          hasAllOf: {},
        },
      };
    case "hasNoneOf":
      return {
        function: {
          oneofKind: "hasNoneOf",
          hasNoneOf: {},
        },
      };
    case "greaterThan":
      return {
        function: {
          oneofKind: "greaterThan",
          greaterThan: {},
        },
      };
    case "greaterThanOrEquals":
      return {
        function: {
          oneofKind: "greaterThanOrEquals",
          greaterThanOrEquals: {},
        },
      };
    case "lessThan":
      return {
        function: {
          oneofKind: "lessThan",
          lessThan: {},
        },
      };
    case "lessThanOrEquals":
      return {
        function: {
          oneofKind: "lessThanOrEquals",
          lessThanOrEquals: {},
        },
      };
    case "between":
      return {
        function: {
          oneofKind: "between",
          between: {},
        },
      };
    case "betweenOrEquals":
      return {
        function: {
          oneofKind: "betweenOrEquals",
          betweenOrEquals: {},
        },
      };
    case "contains":
      return {
        function: {
          oneofKind: "contains",
          contains: {},
        },
      };
    case "notContains":
      return {
        function: {
          oneofKind: "notContains",
          notContains: {},
        },
      };
    case "changes":
      return {
        function: {
          oneofKind: "changes",
          changes: {},
        },
      };
    case "allItemsResolved":
      return {
        function: {
          oneofKind: "allItemsResolved",
          allItemsResolved: {},
        },
      };
    case "notAllItemsResolved":
      return {
        function: {
          oneofKind: "notAllItemsResolved",
          notAllItemsResolved: {},
        },
      };
    case "percentCompleteLessThan":
      return {
        function: {
          oneofKind: "percentCompleteLessThan",
          percentCompleteLessThan: {},
        },
      };
    case "percentCompleteGreaterThan":
      return {
        function: {
          oneofKind: "percentCompleteGreaterThan",
          percentCompleteGreaterThan: {},
        },
      };
    case "isComplete":
      return {
        function: {
          oneofKind: "isComplete",
          isComplete: {},
        },
      };
    case "isNotComplete":
      return {
        function: {
          oneofKind: "isNotComplete",
          isNotComplete: {},
        },
      };
    default:
      return {
        function: {
          oneofKind: undefined,
        },
      };
  }
};

const getArgList = (
  conditionFn: ConditionFunctionConfig,
): Array<{ value: ValueConfig | undefined; name: string }> => {
  switch (conditionFn.function.oneofKind) {
    case "isEquals":
      return [{ value: conditionFn.function.isEquals.right, name: "right" }];
    case "isNotEquals":
      return [{ value: conditionFn.function.isNotEquals.right, name: "right" }];
    case "isAnyOf":
      return [{ value: conditionFn.function.isAnyOf.right, name: "right" }];
    case "isNoneOf":
      return [{ value: conditionFn.function.isNoneOf.right, name: "right" }];
    case "isEmpty":
      return [];
    case "isNotEmpty":
      return [];
    case "isAny":
      return [];
    case "hasAnyOf":
      return [
        { value: conditionFn.function.hasAnyOf.haystack, name: "haystack" },
      ];
    case "hasAllOf":
      return [
        { value: conditionFn.function.hasAllOf.haystack, name: "haystack" },
      ];
    case "hasNoneOf":
      return [
        { value: conditionFn.function.hasNoneOf.haystack, name: "haystack" },
      ];
    case "greaterThan":
      return [{ value: conditionFn.function.greaterThan.right, name: "right" }];
    case "greaterThanOrEquals":
      return [
        {
          value: conditionFn.function.greaterThanOrEquals.right,
          name: "right",
        },
      ];
    case "lessThan":
      return [{ value: conditionFn.function.lessThan.right, name: "right" }];
    case "lessThanOrEquals":
      return [
        { value: conditionFn.function.lessThanOrEquals.right, name: "right" },
      ];
    case "between":
      return [
        { value: conditionFn.function.between.min, name: "min" },
        { value: conditionFn.function.between.max, name: "max" },
      ];
    case "betweenOrEquals":
      return [
        { value: conditionFn.function.betweenOrEquals.min, name: "min" },
        { value: conditionFn.function.betweenOrEquals.max, name: "max" },
      ];
    case "contains":
      return [{ value: conditionFn.function.contains.right, name: "right" }];
    case "notContains":
      return [{ value: conditionFn.function.notContains.right, name: "right" }];
    case "changes":
      return [];
    case "allItemsResolved":
      return [];
    case "notAllItemsResolved":
      return [];
    case "percentCompleteLessThan":
      return [
        {
          value: conditionFn.function.percentCompleteLessThan.right,
          name: "right",
        },
      ];
    case "percentCompleteGreaterThan":
      return [
        {
          value: conditionFn.function.percentCompleteGreaterThan.right,
          name: "right",
        },
      ];
    case "isComplete":
      return [];
    case "isNotComplete":
      return [];
    default:
      return [];
  }
};

const getSelectorState = (
  candidate: CandidateStore,
  isOpen: boolean,
  value?: string,
): "active" | "default" | "error" => {
  if (
    candidate.isTriggerSubmitted &&
    candidate.self.trigger.triggerReference?.triggerId ===
      TriggerLabel.matchesCondition &&
    R.isNil(value)
  ) {
    return "error";
  } else if (isOpen) {
    return "active";
  } else {
    return "default";
  }
};
